// Firefight + melee (GDD §4.3/§4.5). Ranged accuracy is modified by cover, distance,
// LOS, the shooter's movement/suppression, discipline/auras, and a heavy penalty for
// firing into a melee. Suppression weapons pin squads and drain morale. Melee-capable
// squads lock models into a visible scrum; disengaging costs a free hit. AoE weapons
// and hero abilities route through applyAoE.

import { angleOf, clamp, dist, norm, type Vec2 } from "../engine/vec";
import { coverMultiplierAt, losInfo } from "./cover";
import type { AbilityDef, Faction, Model, Squad, Structure, WeaponDef } from "./types";
import { TUNE, World } from "./world";

export function stepCombat(world: World, dt: number): void {
  applyAuras(world, dt);
  acquireTargets(world);
  stepMelee(world, dt);
  stepRanged(world, dt);
  stepTurrets(world, dt);
  // decay per-model render timers
  for (const sq of world.squads)
    for (const m of sq.models) {
      if (m.muzzleFlash > 0) m.muzzleFlash -= dt;
      if (m.hitFlash > 0) m.hitFlash -= dt;
    }
}

// ---- auras -----------------------------------------------------------------

function applyAuras(world: World, dt: number): void {
  for (const sq of world.squads) {
    if (sq.dead) continue;
    sq.auraAcc = 0;
    sq.auraResist = 0;
    if (sq.inspireTimer > 0) {
      sq.inspireTimer -= dt;
      sq.auraAcc += 0.15;
      sq.auraResist += 0.15;
    }
    if (sq.chargeBuffTimer > 0) sq.chargeBuffTimer -= dt;
  }
  for (const f of ["legion", "kindled"] as Faction[]) {
    const hero = world.armies[f].hero;
    if (!hero || hero.dead) continue;
    const def = hero.def;
    if (!def.aura) continue;
    const a = def.aura;
    for (const sq of world.squads) {
      if (sq.dead || sq.faction !== f) continue;
      if (dist(sq.center, hero.center) > a.radius) continue;
      if (a.accuracyBonus) sq.auraAcc += a.accuracyBonus;
      if (a.damageResist) sq.auraResist += a.damageResist;
    }
  }
}

// ---- target acquisition (hierarchical: squad → model, GDD §8.2) ------------

function acquireTargets(world: World): void {
  for (const sq of world.squads) {
    if (sq.dead) {
      continue;
    }
    if (sq.broken && !sq.frenzy) {
      sq.targetSquadCache = -1;
      continue;
    }
    // ordered attack target wins if valid & visible
    let target: Squad | null = null;
    if (sq.order.type === "attack" && sq.order.targetSquad !== -1) {
      const t = world.squads.find((s) => s.id === sq.order.targetSquad && !s.dead);
      if (t && dist(sq.center, t.center) < sq.def.sightRange * 1.3) target = t;
    }
    if (!target) {
      // pick nearest visible enemy, biased toward already-suppressed squads
      let best: Squad | null = null;
      let bestScore = Infinity;
      for (const e of world.squads) {
        if (e.dead || e.faction === sq.faction) continue;
        const d = dist(sq.center, e.center);
        if (d > sq.def.sightRange) continue;
        const score = d * (1 - e.suppression * 0.4) * (1 - e.morale / e.maxMorale * 0.15);
        if (score < bestScore) {
          bestScore = score;
          best = e;
        }
      }
      target = best;
    }
    sq.targetSquadCache = target ? target.id : -1;
  }
}

// ---- ranged ----------------------------------------------------------------

function stepRanged(world: World, dt: number): void {
  for (const sq of world.squads) {
    if (sq.dead || (sq.broken && !sq.frenzy)) continue;
    if (sq.frenzy) continue; // frenzied Kindled only melee-charge
    const targetSquad = sq.targetSquadCache !== -1 ? world.squads.find((s) => s.id === sq.targetSquadCache) : null;

    for (const m of sq.models) {
      if (!m.alive || m.meleeTarget !== -1) continue;
      const weapon = m.weaponOverride ?? sq.def.weapon;
      m.weaponCd -= dt;

      let victim: Model | null = null;
      let victimSquad: Squad | null = null;
      if (targetSquad && !targetSquad.dead) {
        victim = nearestFireable(world, m, weapon, targetSquad);
        victimSquad = victim ? targetSquad : null;
      }
      // structure fallback (enables Annihilation): shoot enemy buildings in range
      let structTarget: Structure | null = null;
      if (!victim) structTarget = nearestEnemyStructure(world, sq.faction, m.pos, weapon.range);

      if (m.weaponCd > 0) continue;

      if (victim && victimSquad) {
        fireAtModel(world, sq, m, weapon, victim, victimSquad);
        m.weaponCd = weapon.cooldown;
      } else if (structTarget) {
        fireAtStructure(world, m, weapon, structTarget);
        m.weaponCd = weapon.cooldown;
      }
    }
  }
}

function nearestFireable(world: World, m: Model, weapon: WeaponDef, sqEnemy: Squad): Model | null {
  let best: Model | null = null;
  let bd = weapon.range * weapon.range;
  for (const e of sqEnemy.models) {
    if (!e.alive) continue;
    const d2 = (e.pos.x - m.pos.x) ** 2 + (e.pos.y - m.pos.y) ** 2;
    if (d2 > bd) continue;
    if (losInfo(world, m.pos, e.pos).blocked) continue;
    bd = d2;
    best = e;
  }
  return best;
}

function fireAtModel(world: World, sq: Squad, m: Model, weapon: WeaponDef, victim: Model, victimSquad: Squad): void {
  const d = dist(m.pos, victim.pos);
  m.facing = angleOf({ x: victim.pos.x - m.pos.x, y: victim.pos.y - m.pos.y });
  m.posture = m.posture === "prone" || m.posture === "pinned" ? m.posture : "firing";
  m.muzzleFlash = 0.12;

  const acc = hitChance(world, sq, m, weapon, d, victim, victimSquad);
  const hit = world.rng.next() < acc;

  // tracer / muzzle FX
  world.addEffect({ kind: "tracer", a: { ...m.pos }, b: { ...victim.pos }, t: 0, ttl: 0.09, color: weapon.tracerColor ?? "#ffcf70", size: weapon.aoe ? 2.4 : 1.2 });
  world.addEffect({ kind: "muzzle", a: { ...m.pos }, t: 0, ttl: 0.08, faction: sq.faction });

  // Suppression + morale apply from volume of fire (hit or near-miss).
  applySuppression(victimSquad, weapon, hit);

  if (weapon.aoe && weapon.aoe > 0) {
    applyAoE(world, victim.pos, weapon.aoe, weapon.damage, weapon.moraleDamage, sq.faction, weapon.name.toLowerCase().includes("flame") || weapon.name.toLowerCase().includes("fire") ? "fire" : "explosion");
    return;
  }

  if (hit) {
    dealDamage(world, sq, victim, victimSquad, weapon.damage);
  }
}

function hitChance(world: World, sq: Squad, m: Model, weapon: WeaponDef, d: number, victim: Model, victimSquad: Squad): number {
  let acc = weapon.accuracy;
  // discipline (Legion) + auras
  acc += sq.discipline * 0.2 + sq.auraAcc;
  // distance falloff
  acc *= clamp(1 - (d / weapon.range) * 0.35, 0.5, 1);
  // shooter moving
  if (sq.path.length > 0) acc *= TUNE.movingAccuracy;
  // shooter suppressed
  acc *= 1 - sq.suppression * (1 - TUNE.suppressedAccuracy);
  // target cover (multiplier: <1 reduces, road >1 increases)
  acc *= coverMultiplierAt(world, victim, m.pos);
  // LOS sight penalty (ruins between)
  acc *= 1 - losInfo(world, m.pos, victim.pos).sightPenalty;
  // firing into a melee is unreliable
  if (victim.meleeTarget !== -1) acc *= TUNE.meleeIntoFirePenalty;
  // covered targets in area cover harder to spot
  void victimSquad;
  return clamp(acc, 0.02, 0.97);
}

function dealDamage(world: World, attacker: Squad | null, victim: Model, victimSquad: Squad, raw: number): void {
  let dmg = raw;
  if (victimSquad.def.armor) dmg = Math.max(1, dmg - victimSquad.def.armor);
  // Only models diving to dirt (fleeing/prone) take reduced damage — frenzied Kindled
  // are charging upright and get NO such protection (GDD §4.3).
  if ((victimSquad.broken && !victimSquad.frenzy) || victim.posture === "prone") dmg *= 1 - TUNE.brokenDamageResist;
  dmg *= 1 - Math.min(0.6, victimSquad.auraResist);
  victim.hp -= dmg;
  victim.hitFlash = 0.15;
  world.addEffect({ kind: "blood", a: { ...victim.pos }, t: 0, ttl: 0.4, faction: victimSquad.faction, size: victimSquad.def.role === "vehicle" ? 3 : 1.5 });
  if (victim.hp <= 0 && victim.alive) killModel(world, attacker, victim, victimSquad);
}

export function killModel(world: World, attacker: Squad | null, victim: Model, victimSquad: Squad): void {
  victim.alive = false;
  victim.posture = "dead";
  if (victim.meleeTarget !== -1) {
    const partner = world.modelById.get(victim.meleeTarget);
    if (partner) partner.meleeTarget = -1;
    victim.meleeTarget = -1;
  }
  world.stats.kills++;
  // casualty morale shock
  victimSquad.morale = Math.max(0, victimSquad.morale - victimSquad.maxMorale * 0.06);
  victimSquad.timeSinceHit = 0;
  world.addEffect({ kind: "flash", a: { ...victim.pos }, t: 0, ttl: 0.25, faction: victimSquad.faction });
  if (attacker) {
    if (attacker.faction === "kindled") world.armies.kindled.fervor = Math.min(1, world.armies.kindled.fervor + TUNE.fervorPerKill);
  }
}

function nearestEnemyStructure(world: World, f: Faction, from: Vec2, range: number): Structure | null {
  let best: Structure | null = null;
  let bd = range * range;
  for (const s of world.structures) {
    if (s.faction === f || s.hp <= 0) continue;
    const d = (s.pos.x - from.x) ** 2 + (s.pos.y - from.y) ** 2;
    if (d < bd) {
      bd = d;
      best = s;
    }
  }
  return best;
}

function fireAtStructure(world: World, m: Model, weapon: WeaponDef, s: Structure): void {
  if (losInfo(world, m.pos, s.pos).blocked) return;
  m.facing = angleOf({ x: s.pos.x - m.pos.x, y: s.pos.y - m.pos.y });
  m.muzzleFlash = 0.12;
  m.posture = "firing";
  world.addEffect({ kind: "tracer", a: { ...m.pos }, b: { ...s.pos }, t: 0, ttl: 0.09, color: weapon.tracerColor ?? "#ffcf70" });
  if (world.rng.next() < weapon.accuracy * 0.9) {
    s.hp -= weapon.damage * 1.2;
    world.addEffect({ kind: "explosion", a: { ...s.pos }, t: 0, ttl: 0.3, size: 8 });
  }
}

// ---- suppression + AoE -----------------------------------------------------

function applySuppression(sq: Squad, weapon: WeaponDef, hit: boolean): void {
  if (sq.def.role === "vehicle") {
    sq.suppression = Math.min(0.4, sq.suppression + weapon.suppression * 0.3);
  } else {
    sq.suppression = Math.min(1, sq.suppression + weapon.suppression * (hit ? 1 : 0.6));
  }
  sq.morale = Math.max(0, sq.morale - weapon.moraleDamage * (hit ? 1 : 0.5));
  sq.timeSinceHit = 0;
}

/** Area effect used by AoE weapons, fire, and hero abilities. */
export function applyAoE(world: World, center: Vec2, radius: number, damage: number, morale: number, attackerFaction: Faction, kind: "fire" | "explosion" | "immolate" = "explosion"): void {
  world.addEffect({ kind: kind === "fire" || kind === "immolate" ? "fire" : "explosion", a: { ...center }, t: 0, ttl: kind === "explosion" ? 0.35 : 0.7, size: radius });
  for (const sq of world.squads) {
    if (sq.dead || sq.faction === attackerFaction) continue;
    if (dist(sq.center, center) > radius + 80) continue;
    let anyHit = false;
    for (const m of sq.models) {
      if (!m.alive) continue;
      const d = dist(m.pos, center);
      if (d > radius) continue;
      const falloff = 1 - (d / radius) * 0.6;
      dealDamage(world, null, m, sq, damage * falloff);
      anyHit = true;
    }
    if (anyHit) {
      sq.morale = Math.max(0, sq.morale - morale);
      sq.suppression = Math.min(1, sq.suppression + 0.15);
      sq.timeSinceHit = 0;
    }
  }
  // AoE also damages enemy structures near the blast
  for (const s of world.structures) {
    if (s.faction === attackerFaction || s.hp <= 0) continue;
    if (dist(s.pos, center) <= radius) s.hp -= damage * 0.8;
  }
}

// ---- melee -----------------------------------------------------------------

function stepMelee(world: World, dt: number): void {
  // form new locks: chargers/frenzy/assault reaching an enemy model
  for (const sq of world.squads) {
    if (sq.dead) continue;
    const wantsMelee = sq.frenzy || sq.def.meleeCharger || sq.order.type === "attack";
    if (!wantsMelee && !sq.meleeLock.size) continue;
    const reach = sq.def.meleeWeapon.range + (sq.def.radius ?? 10);
    for (const m of sq.models) {
      if (!m.alive || m.meleeTarget !== -1) continue;
      const enemy = findMeleeTarget(world, sq, m, reach + 26);
      if (enemy) {
        m.meleeTarget = enemy.model.id;
        // drag the enemy model into the lock if it's free
        if (enemy.model.meleeTarget === -1) enemy.model.meleeTarget = m.id;
        sq.meleeLock.add(enemy.squad.id);
        enemy.squad.meleeLock.add(sq.id);
        if (sq.faction === "kindled") world.armies.kindled.fervor = Math.min(1, world.armies.kindled.fervor + TUNE.fervorPerCharge * dt);
      }
    }
  }

  // resolve existing locks
  for (const sq of world.squads) {
    if (sq.dead) continue;
    const disengaging = (sq.broken && !sq.frenzy) || sq.order.type === "move";
    for (const m of sq.models) {
      if (!m.alive || m.meleeTarget === -1) continue;
      const partner = world.modelById.get(m.meleeTarget);
      const partnerSquad = partner ? world.modelOwner.get(partner.id) : undefined;
      if (!partner || !partner.alive || !partnerSquad || partnerSquad.dead) {
        m.meleeTarget = -1;
        continue;
      }
      if (disengaging) {
        // free hit on the way out (GDD §4.5)
        dealDamage(world, partnerSquad, m, sq, partnerSquad.def.meleeWeapon.damage);
        m.meleeTarget = -1;
        if (partner.meleeTarget === m.id) partner.meleeTarget = -1;
        continue;
      }
      // pull the two together into a scrum at melee distance
      const mid = { x: (m.pos.x + partner.pos.x) / 2, y: (m.pos.y + partner.pos.y) / 2 };
      const toMid = norm({ x: mid.x - m.pos.x, y: mid.y - m.pos.y });
      const gap = dist(m.pos, partner.pos);
      const want = sq.def.meleeWeapon.range;
      if (gap > want) {
        const step = Math.min(gap - want, sq.def.moveSpeed * 1.3 * dt);
        m.pos.x += toMid.x * step;
        m.pos.y += toMid.y * step;
      }
      m.facing = angleOf({ x: partner.pos.x - m.pos.x, y: partner.pos.y - m.pos.y });
      m.posture = sq.frenzy ? "frenzied" : "melee";

      // strike on cooldown
      m.weaponCd -= dt;
      if (m.weaponCd <= 0) {
        const mw = sq.def.meleeWeapon;
        let dmg = mw.damage;
        if (m.isSergeant) dmg *= 1.4;
        if (sq.faction === "kindled") dmg *= 1 + world.armies.kindled.fervorTier * 0.1 + (sq.chargeBuffTimer > 0 ? 0.3 : 0);
        if (world.rng.next() < mw.accuracy) {
          dealDamage(world, sq, partner, partnerSquad, dmg);
          partnerSquad.morale = Math.max(0, partnerSquad.morale - mw.moraleDamage);
        }
        world.addEffect({ kind: "dust", a: { ...mid }, t: 0, ttl: 0.25, size: 6 });
        m.weaponCd = mw.cooldown;
      }
    }
    // clean stale lock set entries
    if (sq.meleeLock.size) {
      const stillLocked = new Set<number>();
      for (const mm of sq.models) if (mm.alive && mm.meleeTarget !== -1) {
        const owner = world.modelOwner.get(mm.meleeTarget);
        if (owner) stillLocked.add(owner.id);
      }
      sq.meleeLock = stillLocked;
    }
  }
}

function findMeleeTarget(world: World, sq: Squad, m: Model, reach: number): { model: Model; squad: Squad } | null {
  let best: { model: Model; squad: Squad } | null = null;
  let bd = reach * reach;
  for (const e of world.squads) {
    if (e.dead || e.faction === sq.faction) continue;
    for (const em of e.models) {
      if (!em.alive) continue;
      const d2 = (em.pos.x - m.pos.x) ** 2 + (em.pos.y - m.pos.y) ** 2;
      if (d2 < bd) {
        bd = d2;
        best = { model: em, squad: e };
      }
    }
  }
  return best;
}

// ---- turrets ---------------------------------------------------------------

function stepTurrets(world: World, dt: number): void {
  for (const s of world.structures) {
    if (s.kind !== "turret" || !s.weapon || s.hp <= 0) continue;
    s.weaponCd -= dt;
    const enemy = world.nearestEnemySquad(s.faction, s.pos, s.weapon.range);
    if (!enemy) continue;
    const victim = enemy.models.find((mm) => mm.alive);
    if (!victim) continue;
    if (losInfo(world, s.pos, victim.pos).blocked) continue;
    if (s.weaponCd <= 0) {
      world.addEffect({ kind: "tracer", a: { ...s.pos }, b: { ...victim.pos }, t: 0, ttl: 0.09, color: s.weapon.tracerColor });
      applySuppression(enemy, s.weapon, true);
      if (world.rng.next() < s.weapon.accuracy * coverMultiplierAt(world, victim, s.pos)) {
        dealDamage(world, null, victim, enemy, s.weapon.damage);
      }
      s.weaponCd = s.weapon.cooldown;
    }
  }
}

// ---- hero abilities --------------------------------------------------------

export function applyAbility(world: World, hero: Squad, ability: AbilityDef, targetPos: Vec2 | null): boolean {
  if ((hero.abilityCd[ability.id] ?? 0) > 0) return false;
  const enemy = world.enemyOf(hero.faction);
  switch (ability.kind) {
    case "rally": {
      const r = ability.radius ?? 260;
      for (const sq of world.squads) {
        if (sq.dead || sq.faction !== hero.faction) continue;
        if (dist(sq.center, hero.center) > r) continue;
        sq.broken = false;
        sq.frenzy = false;
        sq.suppression = Math.max(0, sq.suppression - 0.6);
        sq.morale = Math.min(sq.maxMorale, sq.morale + (ability.value ?? 55));
      }
      world.addEffect({ kind: "flash", a: { ...hero.center }, t: 0, ttl: 0.6, size: r, faction: hero.faction });
      break;
    }
    case "inspire": {
      const r = ability.radius ?? 260;
      for (const sq of world.squads) {
        if (sq.dead || sq.faction !== hero.faction) continue;
        if (dist(sq.center, hero.center) > r) continue;
        sq.inspireTimer = ability.duration ?? 12;
      }
      world.addEffect({ kind: "flash", a: { ...hero.center }, t: 0, ttl: 0.6, size: r, faction: hero.faction });
      break;
    }
    case "artillery": {
      if (!targetPos) return false;
      applyAoE(world, targetPos, ability.radius ?? 130, ability.value ?? 130, 30, hero.faction, "explosion");
      break;
    }
    case "immolate": {
      if (!targetPos) return false;
      applyAoE(world, targetPos, ability.radius ?? 150, ability.value ?? 100, 40, hero.faction, "immolate");
      break;
    }
    case "charge_buff": {
      for (const sq of world.squads) {
        if (sq.dead || sq.faction !== hero.faction) continue;
        sq.chargeBuffTimer = ability.duration ?? 12;
      }
      world.armies[hero.faction].fervor = Math.min(1, world.armies[hero.faction].fervor + (ability.value ?? 0.35));
      break;
    }
  }
  void enemy;
  hero.abilityCd[ability.id] = ability.cooldown;
  return true;
}
